import React from "react";
import i18n from "locales/i18n";

/**
 * Represents a navigation item with an icon, label, and route.
 * @typedef {Object} NavItem
 * @property {string} icon - The CSS class for the icon.
 * @property {string[] | JSX.Element[]} label - The translated label using i18n, can be an array or JSX element.
 * @property {string} [to] - The target route for the navigation item (optional if content is present).
 * @property {NavItem[]} [content] - Optional nested navigation items.
 * @type {NavItem[]}
 */

/**
 * Permission context used by dynamic sidebar permission callbacks.
 * @typedef {Object} PermissionContext
 * @property {boolean} isAdmin - Indicates whether the current user is an administrator.
 * @property {boolean} access - Indicates whether the user can view managed sections.
 * @property {boolean} accessTRD - Access to TRD configuration option.
 * @property {boolean} accessDocumentaryType - Access to documentary types option.
 * @property {boolean} accessConfigurationApp - Access to application setup option.
 * @property {boolean} accessDesignerForms - Access to create/edit forms designer.
 * @property {boolean} accessDesignerFormsConsult - Access to consult forms designer.
 * @property {boolean} accessWorkflowsForms - Access to create/edit workflow designer.
 * @property {boolean} accessWorkflowsFormsConsult - Access to consult workflow designer.
 * @property {boolean} accessAdminThirdPartyAccounts - Access to third-party accounts option.
 * @property {boolean} accessList - Access to list designer.
 * @property {boolean} accessUserManager - Access to user management options.
 * @property {boolean} accessRoles - Access to role management options.
 * @property {boolean} accessIntegrations - Access to integrations section.
 * @property {boolean} accessWorkGroups - Access to workgroup management options.
 * @property {boolean} accessProcessManagement - Access to process management option.
 * @property {boolean} accessMetrics - Access to metrics option.
 * @property {boolean} accessAuditLogs - Access to audit logs option.
 * @property {boolean} accessTemplateDesigner - Access to templates designer option.
 */

/**
 * Permission callback used to evaluate whether a menu item should be displayed.
 * @callback PermissionRule
 * @param {PermissionContext} ctx - Current user permission context.
 * @returns {boolean} True when the item should be visible.
 */
/**
 * Dashboard navigation entry.
 * @type {NavItem[]}
 */
export let DashboardNav = [
  {
    icon: "pe-7s-home",
    labelKey: "startup",
    to: "/dashboard",
  },
];

/**
 * Create record navigation entry.
 * @type {NavItem[]}
 */
export let CreateRecordNav = [
  {
    icon: "pe-7s-pen",
    labelKey: "recordData.heading",
    to: "/record/list/forms",
  },
];

/**
 * Record consultation navigation entry.
 * @type {NavItem[]}
 */
export let ConsultRecordNav = [
  {
    icon: "pe-7s-search",
    labelKey: "dashboard.label7",
    content: [
      {
        labelKey: "recordList1",
        to: "/record/list/form",
      },
    ],
  },
];

/**
 * Record consultation navigation entry including TRD explorer access.
 * @type {NavItem[]}
 */
export let ConsultRecordNavTrdExplorer = [
  {
    icon: "pe-7s-search",
    labelKey: "dashboard.label7",
    content: [
      {
        labelKey: "recordList1",
        to: "/record/list/form",
      },
      {
        labelKey: "navItems.aplication.ConsultTRD",
        to: "/trd/expedients/consultation",
      },
    ],
  },
];

/**
 * Task inbox navigation entry.
 * @type {NavItem[]}
 */
export let TaskBoxNav = [
  {
    icon: "pe-7s-drawer",
    labelKey: "taskBox",
    to: "/taskBox",
  },
];

/**
 * Reports navigation entry.
 * @type {NavItem[]}
 */
export let ReportsNav = [
  {
    icon: "pe-7s-news-paper",
    labelKey: "info.SignatureNav",
    content: [
      {
        labelKey: "info.SignatureNav2",
        to: "/reports/signatures",
      },
    ],
  },
];

/**
 * Process management navigation entry.
 * @type {NavItem[]}
 */
export let ProcessManagementNav = [
  {
    icon: "pe-7s-network",
    labelKey: "processManagement.Header",
    to: "/process/management",
  },
];

/**
 * Trash navigation entry.
 * @type {NavItem[]}
 */
export let TrashNav = [
  {
    icon: "pe-7s-trash",
    labelKey: "trash.label1",
    to: "/trash",
  },
];

/**
 * Data export navigation entry and sub-options.
 * @type {NavItem[]}
 */
export let DataExportNav = [
  {
    icon: "pe-7s-download",
    labelKey: "export.records.nav.title",
    content: [
      {
        labelKey: "export.records.nav",
        to: "/export-data",
      },
      {
        label: () => i18n.t("export.records.navProcess"),
        to: "/export-data/process",
        permission: (ctx) => ctx.isAdmin || ctx.accessMetrics,
      },
    ],
  },
];

/**
 * Single ParameterNav with dynamic permission rules.
 * Each item shows/hides based on permission function.
 * @type {NavItem[]}
 */
export let ParameterNav = [
  {
    icon: "pe-7s-config",
    label: () => (
      <div>
        {i18n.t("navItems.aplication.Parameters")}
        {"  "}
        <span className="badge badge-success badge-small"> {i18n.t("dashboard.label6")}</span>
      </div>
    ),
    content: [
      {
        labelKey: "navItems.aplication.TrdOption1",
        to: "/trd",
        permission: (ctx) => ctx.isAdmin || ctx.accessTRD,
      },
      {
        labelKey: "navItems.aplication.documentalTypes",
        to: "/documentary-types/list",
        permission: (ctx) => ctx.isAdmin || ctx.accessDocumentaryType,
      },
      {
        label: () => (
          <div>
            {i18n.t("navItems.aplication.Config")}
            {"  "}
            <span className="badge badge-success badge-small"> {i18n.t("dashboard.label6")}</span>
          </div>
        ),
        to: "/application/config/parameters",
        permission: (ctx) => ctx.isAdmin || ctx.accessConfigurationApp,
      },
    ],
  },
];

/**
 * Single FormsDesignerNav with dynamic permission rules.
 * @type {NavItem[]}
 */
export let FormsDesignerNav = [
  {
    icon: "pe-7s-note",
    labelKey: "form.designerItem1",
    content: [
      {
        labelKey: "form.designerItem2",
        to: "/formsdesigner/create",
        permission: (ctx) => ctx.isAdmin || ctx.accessDesignerForms,
      },
      {
        labelKey: "form.designerItem3",
        to: "/formsdesigner/list",
        permission: (ctx) =>
          ctx.isAdmin || ctx.accessDesignerForms || ctx.accessDesignerFormsConsult,
      },
    ],
  },
];

/**
 * List designer navigation entry.
 * @type {NavItem[]}
 */
export let ListDesignerNav = [
  {
    icon: "pe-7s-menu",
    labelKey: "list.designerItem1",
    content: [
      {
        labelKey: "list.designerItem2",
        to: "/formsdesigner/createlist",
      },
      {
        labelKey: "list.designerItem3",
        to: "/formsdesigner/consultlist",
      },
    ],
  },
];

/**
 * Unified Designer navigation entry with consult-only options.
 * Groups Forms, Workflow, List, and Template designers.
 * @type {NavItem[]}
 */
export let DesignerNav = [
  {
    icon: "pe-7s-note",
    labelKey: "designer.title",
    content: [
      {
        labelKey: "form.designerItem1",
        to: "/formsdesigner/list",
        permission: (ctx) =>
          ctx.isAdmin || ctx.accessDesignerForms || ctx.accessDesignerFormsConsult,
      },
      {
        labelKey: "createWorflow.titleNav",
        to: "/WorkFlow/list",
        permission: (ctx) =>
          ctx.isAdmin || ctx.accessWorkflowsForms || ctx.accessWorkflowsFormsConsult,
      },
      {
        labelKey: "list.designerItem1",
        to: "/formsdesigner/consultlist",
        permission: (ctx) => ctx.isAdmin || ctx.accessList,
      },
      {
        labelKey: "templatesDesigner.title",
        to: "/templates/list",
        permission: (ctx) => ctx.isAdmin || ctx.accessTemplateDesigner,
      },
    ],
  },
];

/**
 * Single AdminNav with dynamic permission rules.
 * Each content item has a permission function that determines visibility.
 * The permission function receives the context with all access flags.
 * @type {NavItem[]}
 */
export let AdminNav = [
  {
    icon: "pe-7s-users",
    labelKey: "navItems.aplication.AdminUsers",
    content: [
      {
        labelKey: "navItems.aplication.AdminUsersOption2",
        to: "/user/create",
        permission: (ctx) => ctx.isAdmin || ctx.accessUserManager,
      },
      {
        labelKey: "navItems.aplication.AdminUsersOption1",
        to: "/user/list",
        permission: (ctx) => ctx.isAdmin || ctx.accessUserManager,
      },
      {
        labelKey: "navItems.aplication.AdminUsersOption4",
        to: "/user/roles",
        permission: (ctx) => ctx.isAdmin || ctx.accessRoles,
      },
      {
        labelKey: "create.work.group.nav",
        to: "/user/workgroup",
        permission: (ctx) => ctx.isAdmin || ctx.accessWorkGroups,
      },
      {
        labelKey: "navItems.aplication.AdminUsersOption5",
        to: "/user/Third-party-accounts",
        permission: (ctx) => ctx.isAdmin || ctx.accessAdminThirdPartyAccounts,
      },
    ],
  },
];

/**
 * Single WorkFlowDesignerNav with dynamic permission rules.
 * @type {NavItem[]}
 */
export let WorkFlowDesignerNav = [
  {
    icon: "pe-7s-note",
    labelKey: "createWorflow.titleNav",
    content: [
      {
        labelKey: "createWorflow.SubtitleNav",
        to: "/WorkFlow",
        permission: (ctx) => ctx.isAdmin || ctx.accessWorkflowsForms,
      },
      {
        labelKey: "createWorflow.query",
        to: "/WorkFlow/list",
        permission: (ctx) =>
          ctx.isAdmin || ctx.accessWorkflowsForms || ctx.accessWorkflowsFormsConsult,
      },
    ],
  },
];

/**
 * Document templates designer navigation entry.
 * @type {NavItem[]}
 */
export let DocumentTemplatesDesignerNav = [
  {
    icon: "pe-7s-display2",
    labelKey: "templatesDesigner.title",
    content: [
      {
        labelKey: "templatesDesigner.quey",
        to: "/templates/list",
      },
    ],
  },
];

/**
 * Single MetricsNav with dynamic permission rules.
 * Each content item has a permission function that determines visibility.
 * @type {NavItem[]}
 */
export let MetricsNav = [
  {
    icon: "pe-7s-graph3",
    label: () => (
      <div>
        {i18n.t("AuditLogs")}
        {"  "}
        <span className="badge badge-success badge-small"> {i18n.t("dashboard.label6")}</span>
      </div>
    ),
    content: [
      {
        label: () => (
          <div>
            {i18n.t("metrics")}
            {"  "}
            <span className="badge badge-success badge-small"> {i18n.t("dashboard.label6")}</span>
          </div>
        ),
        to: "/metrics",
        permission: (ctx) => ctx.isAdmin || ctx.accessMetrics,
      },
      {
        labelKey: "AuditLogs.nav",
        to: "/audit/logs",
        permission: (ctx) => ctx.isAdmin || ctx.accessAuditLogs,
      },
    ],
  },
];

/**
 * Integrations navigation entry.
 * @type {NavItem[]}
 */
export let IntegrationsNav = [
  {
    icon: "pe-7s-keypad",
    label: () => (
      <div>
        {i18n.t("integrations.nav")}
        {"  "}
        <span className="badge badge-success badge-small"> {i18n.t("dashboard.label6")}</span>
      </div>
    ),
    to: "/integrations",
  },
];

// Grouped exports to keep navs organized by domain without changing existing contracts.
export const NAV_BASE_SECTION = {
  dashboard: DashboardNav,
  createRecord: CreateRecordNav,
  consultRecord: ConsultRecordNav,
  consultRecordTrdExplorer: ConsultRecordNavTrdExplorer,
  taskBox: TaskBoxNav,
  reports: ReportsNav,
  processManagement: ProcessManagementNav,
  trash: TrashNav,
  dataExport: DataExportNav,
};

export const NAV_PARAMETERS_SECTION = {
  parameter: ParameterNav,
};

export const NAV_DESIGNERS_SECTION = {
  formsDesigner: FormsDesignerNav,
  listDesigner: ListDesignerNav,
  workflowDesigner: WorkFlowDesignerNav,
  templatesDesigner: DocumentTemplatesDesignerNav,
  designer: DesignerNav,
};

export const NAV_ADMIN_SECTION = {
  admin: AdminNav,
};

export const NAV_ANALYTICS_SECTION = {
  metrics: MetricsNav,
};

export const NAV_INTEGRATIONS_SECTION = {
  integrations: IntegrationsNav,
};
